import './index.css';

import React, { useEffect } from 'react';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements
} from 'react-router-dom';

import App from './app/App';
import AppOffline from './AppOffline';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import Config from './shared/config';
import ExternalTool from './externaltool/ExternalTool';
import Membership from './membership/App';
import ReactGA from 'react-ga4';
import SkinThemeService from './shared/services/SkinThemeService';
import SplashScreen from './app/components/SplashScreen';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { ThemeProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import useStatus from './shared/hooks/useStatus';
import useThemes from './shared/hooks/useThemes';

Bugsnag.start({
  apiKey: process.env.BUGSNAG_API_KEY,
  plugins: [new BugsnagPluginReact()],
  releaseStage: Config.ENV,
  enabledReleaseStages: ['dev', 'staging', 'live']
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

/**
 * Google Analytics 4 loaded from skin theme asset.
 */
const useSkinThemeAnalytics = () => {
  const { skinTheme } = useThemes();
  const analyticsAccount = SkinThemeService.getThemeOptionValue(
    skinTheme,
    'container',
    'analyticsAccount'
  );
  useEffect(() => {
    if (analyticsAccount) {
      ReactGA.initialize(analyticsAccount);
      ReactGA.send('pageview');
    }
  }, [analyticsAccount]);
};

interface MultiRoutes {
  paths: string[];
  element: JSX.Element;
}
export const renderMultiRoutes = ({ element: Element, paths }: MultiRoutes) =>
  paths.map((path) => <Route key={path} path={path} element={Element} />);

// Use createBrowserRouter to support useBlocker
// https://reactrouter.com/en/main/routers/picking-a-router
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {renderMultiRoutes({
        paths: [
          '/registration',
          '/request-password-reset',
          '/reset-my-password',
          '/verify-email',
          '/request-verification-email',
          '/invitation'
        ],
        element: <Membership />
      })}
      <Route path="/e/*" element={<ExternalTool />} />
      <Route path="/*" element={<App />} />
    </>
  )
);

/**
 * Container component once the App is running and not offline.
 */
const AppOnline = () => {
  const { muiTheme } = useThemes();
  useSkinThemeAnalytics();

  return (
    <ErrorBoundary>
      {/* Using StylesProvider with injectFirst to override MUI styles with CSS */}
      {/* https://material-ui.com/styles/advanced/#css-injection-order */}
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={muiTheme || {}}>
          <RouterProvider router={router} />
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
};

/**
 * Wrapper to catch App Offline messages.
 */
const MemoComponent = React.memo(
  (props: { pending: boolean; appOffline: string }) => {
    const { pending, appOffline } = props;
    return pending ? (
      <SplashScreen />
    ) : appOffline ? (
      <AppOffline message={appOffline} />
    ) : (
      <AppOnline />
    );
  },
  (prev, next) => next.appOffline === prev.appOffline
);

const Component = () => {
  const { pending, appOffline } = useStatus();
  return <MemoComponent pending={pending} appOffline={appOffline} />;
};

const root = createRoot(document.getElementById('app') as HTMLElement);
root.render(<Component />);
